import moment from "moment";

import { SYNTHETIC_APPLICATION_SOURCES } from "cortex/ui/src/constant/application";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { name } from "cortex/ui/src/redux-store/applications/selectors";
import { getName } from "cortex/ui/src/redux-store/devices/selectors";
import { DEFAULT_TIME_FORMAT, utcToLocalFormat } from "cortex/ui/src/util/time";

export const formatDateTime = (
  date: string | moment.Moment,
  format?: string,
): string => {
  if (!date) {
    return "N/A";
  }
  return moment(date).format(format || DEFAULT_TIME_FORMAT);
};

export const formatDateTimeLocal = (
  date: string | moment.Moment,
  format?: string,
): string => {
  if (!date) {
    return "N/A";
  }
  return utcToLocalFormat(date, format || DEFAULT_TIME_FORMAT);
};

export function formatRelativeTime(value: string): string {
  if (!value) {
    return "N/A";
  }

  const now = moment.utc();
  const seen = moment.utc(value);

  if (!seen.isValid()) {
    return "N/A";
  }

  if (seen.isAfter(now)) return "Just now";

  const minutes = now.diff(seen, "minutes");
  const hours = now.diff(seen, "hours");
  const days = now.diff(seen, "days");
  const weeks = now.diff(seen, "weeks");
  const months = now.diff(seen, "months");
  const years = now.diff(seen, "years");

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  if (weeks < 5) return `${weeks}w`;
  if (months < 12) return `${months}mo`;
  return `${years}y`;
}

export const formatDeviceName = (
  id: number,
  devices: Record<string, Device>,
) => {
  const device = devices[id];
  return device ? getName(device) : `Unknown device (${id})`;
};

export const formatApplicationSource = (
  applications: Record<string, Application>,
  sourceId?: number | string | null,
) => {
  const nSourceId = Number(sourceId);

  if (!sourceId || Number.isNaN(nSourceId)) {
    return "Player";
  }

  const syntheticLabel = SYNTHETIC_APPLICATION_SOURCES[nSourceId];
  if (syntheticLabel) {
    return syntheticLabel;
  }

  if (nSourceId < 0) {
    return "Player";
  }

  const sSourceId = String(sourceId);
  const app = applications[sSourceId];

  return app ? name(app) : `Unknown app (${sSourceId})`;
};

export const formatPercentage = (value: number, fixed: number = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return "N/A";
  }
  return `${value.toFixed(fixed)}%`;
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  return `${value % 1 === 0 ? value : value.toFixed(2)} ${units[i]}`;
};
